import type { Category, Product, ProductReview } from '../types';
import { normalizeProductMediaType } from '../utils/productMedia';

export interface CatalogQuery {
  search?: string;
  category?: string;
  audience?: 'kids' | 'adult' | 'public';
  sort?: string;
  page?: number;
  limit?: number;
}

export interface CategoryMutationPayload {
  id?: string;
  name: string;
  slug?: string;
  audience?: Category['audience'];
}

export interface ProductMutationPayload {
  id?: string;
  name?: string;
  nameKey?: string;
  title?: string;
  titleKey?: string;
  description?: string;
  descriptionKey?: string;
  categoryId?: string;
  categoryKey?: string;
  audience?: Product['audience'] | 'adult' | 'mixed';
  language?: string;
  isDigital?: boolean;
  isPODAvailable?: boolean;
  currency?: string;
  mediaType?: Product['mediaType'] | 'written' | 'audiobook' | 'video';
  price?: number;
  imageUrl?: string;
  contentUrl?: string;
  rating?: number;
  reviewCount?: number;
  stock?: number;
}

export type CatalogItemKind = 'book' | 'audio' | 'animation';

export interface CatalogItemCreateInput {
  kind: CatalogItemKind;
  titleKey: string;
  descriptionKey: string;
  categoryKey: string;
  bookFormat?: string;
  audience?: 'public' | 'kids' | 'adult';
  price?: number;
  printPrice?: number;
  currency?: string;
  language?: string;
  isPODAvailable?: boolean;
  podProjectId?: string;
  slug?: string;
  status?: 'draft' | 'published';
  imageUrl?: string;
  contentUrl?: string;
  bookFile?: File;
  audioFile?: File;
  videoFile?: File; // backwards compatibility
  thumbnailFile?: File; // backwards compatibility
  coverFile?: File; // backwards compatibility / animation picture
  trailerFile?: File;
  fullVideoFile?: File;
  pictureFile?: File;
  mediaType?: string; // backwards compatibility
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface CatalogItemCreateResult {
  kind: CatalogItemKind;
  product: Product | null;
}

export interface AnimationUploadPayload {
  titleKey: string;
  descriptionKey: string;
  videoFile: File;
  thumbnailFile?: File;
  categoryKey?: string;
  language?: string;
  audience?: 'kids' | 'adult' | 'public';
}

export interface AudioUploadPayload {
  titleKey: string;
  descriptionKey: string;
  audioFile: File;
  thumbnailFile?: File;
  categoryKey?: string;
  language?: string;
  audience?: 'kids' | 'adult' | 'public';
}

interface CatalogConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

const categoryAliasToTranslationKey: Record<string, string> = {
  children: 'products.categories.children',
  child: 'products.categories.children',
  kids: 'products.categories.children',
  kid: 'products.categories.children',
  adventure: 'products.categories.adventure',
  adventures: 'products.categories.adventure',
  business: 'products.categories.business',
  history: 'products.categories.history',
  poetry: 'products.categories.poetry',
};

const defaultCatalogConfig: CatalogConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000',
  tokenKey: 'haapa.authToken',
  userKey: 'haapa.currentUser',
};

class CatalogService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;
  private categoryCache: Map<string, Category>;

  constructor(config = defaultCatalogConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
    this.categoryCache = new Map<string, Category>();
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private asNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return undefined;
  }

  private asBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value !== 0 : undefined;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized) return undefined;
      if (['true', '1', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no'].includes(normalized)) return false;
    }
    return undefined;
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private getFirstString(...values: unknown[]): string | undefined {
    for (const value of values) {
      const resolved = this.asString(value);
      if (resolved) {
        return resolved;
      }
    }
    return undefined;
  }

  private getFirstNumber(...values: unknown[]): number | undefined {
    for (const value of values) {
      const resolved = this.asNumber(value);
      if (resolved !== undefined) {
        return resolved;
      }
    }
    return undefined;
  }

  private normalizeProductAudience(value: unknown): Product['audience'] | undefined {
    if (value === 'kids') {
      return 'kids';
    }

    if (value === 'adult' || value === 'adults') {
      return 'adults';
    }

    if (value === 'mixed' || value === 'public') {
      return 'public';
    }

    return undefined;
  }

  private normalizeCategoryAudience(value: unknown): Category['audience'] | undefined {
    if (
      value === 'kids' ||
      value === 'adult' ||
      value === 'adults' ||
      value === 'mixed' ||
      value === 'public'
    ) {
      return value;
    }

    return undefined;
  }

  private normalizeCategoryKey(rawCategoryValue: string): string {
    const trimmed = rawCategoryValue.trim();
    if (!trimmed) {
      return 'products.categories.history';
    }

    if (trimmed.startsWith('products.categories.')) {
      return trimmed;
    }

    const normalizedToken = trimmed
      .toLowerCase()
      .replace(/^products\.categories\./, '')
      .replace(/\s+/g, '-')
      .replace(/_/g, '-');
    return categoryAliasToTranslationKey[normalizedToken] ?? trimmed;
  }

  private normalizeCategory(categoryValue: unknown): Category | null {
    const categoryRecord = this.asRecord(categoryValue);
    const id = this.getFirstString(
      categoryRecord.id,
      categoryRecord._id,
      categoryRecord.categoryId,
      categoryRecord.key,
    );
    const name =
      this.getFirstString(
        categoryRecord.name,
        categoryRecord.label,
        categoryRecord.title,
        categoryRecord.slug,
      ) ?? id;

    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      slug: this.getFirstString(categoryRecord.slug),
      audience: this.normalizeCategoryAudience(categoryRecord.audience),
    };
  }

  private normalizeReview(reviewValue: unknown): ProductReview | null {
    const reviewRecord = this.asRecord(reviewValue);
    const author = this.getFirstString(
      reviewRecord.author,
      reviewRecord.userName,
      reviewRecord.username,
      reviewRecord.name,
    );
    const rating = this.getFirstNumber(reviewRecord.rating, reviewRecord.score);
    const commentKey = this.getFirstString(
      reviewRecord.commentKey,
      reviewRecord.comment,
      reviewRecord.content,
      reviewRecord.text,
    );

    if (!author || rating === undefined || !commentKey) {
      return null;
    }

    return {
      author,
      rating,
      commentKey,
    };
  }

  private resolvePersonName(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    const record = this.asRecord(value);
    const fullName = this.getFirstString(
      record.name,
      record.fullname,
      record.fullName,
      record.displayName,
      record.username,
      record.authorName,
      record.creatorName,
      record.vendorName,
      record.publisherName,
    );

    if (fullName) {
      return fullName;
    }

    const firstName = this.asString(record.firstName);
    const lastName = this.asString(record.lastName);
    if (firstName || lastName) {
      return `${firstName ?? ''} ${lastName ?? ''}`.trim();
    }

    return undefined;
  }

  private toAbsoluteUrl(path: string): string {
    const sanitizedBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const sanitizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${sanitizedBaseUrl}${sanitizedPath}`;
  }

  private getStoredUser(): Record<string, unknown> | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const raw = window.localStorage.getItem(this.userKey);
    if (!raw) {
      return null;
    }

    try {
      return this.asRecord(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private buildHeaders(includeBody: boolean, requireAuth: boolean): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (includeBody) {
      headers['Content-Type'] = 'application/json';
    }

    if (!requireAuth) {
      return headers;
    }

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const storedUser = this.getStoredUser();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const userId = this.asString(storedUser?.id);
    if (userId) {
      headers['x-user-id'] = userId;
    }

    const role = this.asString(storedUser?.role);
    if (role) {
      headers['x-user-role'] = role;
    }

    const audience = this.getFirstString(storedUser?.audience, storedUser?.audienceProfile);
    if (audience) {
      headers['x-user-audience'] = audience;
    }

    return headers;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const payloadRecord = this.asRecord(payload);
    return (
      this.getFirstString(payloadRecord.message, payloadRecord.error) ??
      fallback
    );
  }

  private unwrapPayloadData(payload: unknown): unknown {
    const payloadRecord = this.asRecord(payload);
    if ('data' in payloadRecord) {
      return payloadRecord.data;
    }
    return payload;
  }

  private resolveListSource(payload: unknown, keys: string[]): unknown[] {
    const unwrappedData = this.unwrapPayloadData(payload);
    if (Array.isArray(unwrappedData)) {
      return unwrappedData;
    }

    const unwrappedRecord = this.asRecord(unwrappedData);
    for (const key of keys) {
      const listCandidate = this.asArray(unwrappedRecord[key]);
      if (listCandidate.length > 0) {
        return listCandidate;
      }
    }

    if (Object.keys(unwrappedRecord).length > 0) {
      // Some endpoints return a single object instead of an array.
      return [unwrappedData];
    }

    return [];
  }

  private normalizeCategoryList(payload: unknown): Category[] {
    const listSource = this.resolveListSource(payload, ['categories', 'items', 'results']);
    const normalizedCategories = listSource
      .map((categoryValue) => this.normalizeCategory(categoryValue))
      .filter((category): category is Category => Boolean(category));
    const deduped = new Map<string, Category>();
    normalizedCategories.forEach((category) => {
      deduped.set(category.id, category);
    });
    return [...deduped.values()];
  }

  private normalizeProduct(productValue: unknown): Product | null {
    const wrapperRecord = this.asRecord(productValue);
    const nestedProductRecord = this.asRecord(wrapperRecord.product);
    const hasNestedProduct = Object.keys(nestedProductRecord).length > 0;
    const productRecord = hasNestedProduct ? nestedProductRecord : wrapperRecord;
    const id = this.getFirstString(
      productRecord.id,
      productRecord._id,
      productRecord.productId,
      productRecord.product_id,
      wrapperRecord.id,
      wrapperRecord._id,
      wrapperRecord.productId,
    );
    if (!id) {
      return null;
    }

    const inlineCategory = this.normalizeCategory(productRecord.category);
    const categoryId = this.getFirstString(
      productRecord.categoryId,
      productRecord.category_id,
      inlineCategory?.id,
    );
    const cachedCategory = categoryId ? this.categoryCache.get(categoryId) : undefined;
    const categoryKey = this.normalizeCategoryKey(
      this.getFirstString(
        productRecord.categoryKey,
        productRecord.categorySlug,
        inlineCategory?.slug,
        cachedCategory?.slug,
        productRecord.categoryName,
        inlineCategory?.name,
        cachedCategory?.name,
        categoryId,
      ) ?? 'products.categories.history',
    );

    const reviews = this.asArray(productRecord.reviews)
      .map((reviewValue) => this.normalizeReview(reviewValue))
      .filter((review): review is ProductReview => Boolean(review));
    const reviewCount = this.getFirstNumber(productRecord.reviewCount, productRecord.reviewsCount) ?? reviews.length;
    const creatorName =
      this.getFirstString(
        productRecord.authorName,
        productRecord.author,
        productRecord.creatorName,
        productRecord.vendorName,
        productRecord.publisherName,
        productRecord.uploaderName,
      ) ||
      this.resolvePersonName(productRecord.author) ||
      this.resolvePersonName(productRecord.creator) ||
      this.resolvePersonName(productRecord.vendor) ||
      this.resolvePersonName(productRecord.publisher) ||
      this.resolvePersonName(productRecord.uploadedBy) ||
      this.resolvePersonName(productRecord.createdBy) ||
      this.getFirstString(
        wrapperRecord.authorName,
        wrapperRecord.author,
        wrapperRecord.creatorName,
        wrapperRecord.vendorName,
        wrapperRecord.publisherName,
      ) ||
      this.resolvePersonName(wrapperRecord.author) ||
      this.resolvePersonName(wrapperRecord.creator) ||
      this.resolvePersonName(wrapperRecord.vendor) ||
      this.resolvePersonName(wrapperRecord.publisher) ||
      undefined;

    return {
      id,
      nameKey:
        this.getFirstString(
          productRecord.nameKey,
          productRecord.titleKey,
          productRecord.name,
          productRecord.title,
        ) ?? 'Untitled Product',
      descriptionKey:
        this.getFirstString(
          productRecord.descriptionKey,
          productRecord.description,
          productRecord.summary,
        ) ?? 'No description available.',
      categoryKey,
      categoryId,
      audience:
        this.normalizeProductAudience(productRecord.audience) ??
        this.normalizeProductAudience(productRecord.targetAudience) ??
        this.normalizeProductAudience(inlineCategory?.audience) ??
        this.normalizeProductAudience(cachedCategory?.audience),
      contentUrl:
        this.getFirstString(
          productRecord.contentUrl,
          productRecord.fileUrl,
          productRecord.downloadUrl,
          productRecord.streamUrl,
          productRecord.audioUrl,
          productRecord.videoUrl,
        ) ?? undefined,
      audioUrl: this.getFirstString(productRecord.audioUrl) ?? undefined,
      videoUrl: this.getFirstString(productRecord.videoUrl) ?? undefined,
      mediaType:
        normalizeProductMediaType(productRecord.mediaType) ??
        normalizeProductMediaType(productRecord.contentType) ??
        normalizeProductMediaType(productRecord.format) ??
        normalizeProductMediaType(productRecord.type) ??
        normalizeProductMediaType(productRecord.kind) ??
        normalizeProductMediaType(wrapperRecord.kind) ??
        normalizeProductMediaType(wrapperRecord.mediaType) ??
        normalizeProductMediaType(wrapperRecord.contentType) ??
        normalizeProductMediaType(wrapperRecord.format) ??
        normalizeProductMediaType(wrapperRecord.type),
      author: creatorName,
      authorName: creatorName,
      price: this.getFirstNumber(productRecord.price, productRecord.unitPrice, productRecord.amount) ?? 0,
      printPrice: this.getFirstNumber(productRecord.printPrice, productRecord.print_price) ?? undefined,
      currency: this.getFirstString(productRecord.currency) ?? undefined,
      imageUrl:
        this.getFirstString(
          productRecord.imageUrl,
          productRecord.image,
          productRecord.coverUrl,
          productRecord.thumbnailUrl,
        ) ?? '/vite.svg',
      rating: this.getFirstNumber(productRecord.rating, productRecord.avgRating, productRecord.averageRating) ?? 0,
      reviewCount,
      stock: this.getFirstNumber(productRecord.stock, productRecord.quantity, productRecord.inventory) ?? 0,
      isDigital: this.asBoolean(productRecord.isDigital) ?? undefined,
      isPODAvailable: this.asBoolean(productRecord.isPODAvailable) ?? undefined,
      podProjectId: this.getFirstString(productRecord.podProjectId, productRecord.pod_project_id) ?? undefined,
      reviews,
    };
  }

  private normalizeProductList(payload: unknown): Product[] {
    const listSource = this.resolveListSource(payload, ['products', 'items', 'results']);
    const normalizedProducts = listSource
      .map((productValue) => this.normalizeProduct(productValue))
      .filter((product): product is Product => Boolean(product));
    const deduped = new Map<string, Product>();
    normalizedProducts.forEach((product) => {
      deduped.set(product.id, product);
    });
    return [...deduped.values()];
  }

  private normalizeSingleProduct(payload: unknown): Product | null {
    const unwrappedData = this.unwrapPayloadData(payload);
    const dataRecord = this.asRecord(unwrappedData);
    const candidate = dataRecord.product && dataRecord.kind ? unwrappedData : dataRecord.product ?? unwrappedData;
    return this.normalizeProduct(candidate);
  }

  private normalizeSingleCategory(payload: unknown): Category | null {
    const unwrappedData = this.unwrapPayloadData(payload);
    const dataRecord = this.asRecord(unwrappedData);
    const candidate = dataRecord.category ?? unwrappedData;
    return this.normalizeCategory(candidate);
  }

  private async postFormDataWithProgress(
    path: string,
    formData: FormData,
    onUploadProgress?: (progress: UploadProgress) => void,
    requireAuth = false,
  ): Promise<unknown> {
    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.toAbsoluteUrl(path), true);

      const headers = this.buildHeaders(false, requireAuth);
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.upload.onprogress = (event) => {
        if (!onUploadProgress || !event.lengthComputable) {
          return;
        }
        const percent = event.total > 0 ? Math.min(100, (event.loaded / event.total) * 100) : 0;
        onUploadProgress({ loaded: event.loaded, total: event.total, percent });
      };

      xhr.onerror = () => reject(new Error('Network error while uploading product.'));
      xhr.onabort = () => reject(new Error('Upload aborted.'));
      xhr.onload = () => {
        let payload: unknown = null;
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          payload = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload);
          return;
        }

        const message = this.toErrorMessage(payload, `Request failed with status ${xhr.status}`);
        reject(new Error(message));
      };

      xhr.send(formData);
    });
  }

  private buildQueryString(query?: CatalogQuery): string {
    if (!query) {
      return '';
    }

    const params = new URLSearchParams();
    if (query.search) {
      params.set('search', query.search);
    }
    if (query.category) {
      params.set('category', query.category);
    }
    if (query.audience) {
      params.set('audience', query.audience);
    }
    if (query.sort) {
      params.set('sort', query.sort);
    }
    if (typeof query.page === 'number') {
      params.set('page', String(query.page));
    }
    if (typeof query.limit === 'number') {
      params.set('limit', String(query.limit));
    }

    const encoded = params.toString();
    return encoded ? `?${encoded}` : '';
  }

  private async request(path: string, init?: RequestInit, requireAuth = false): Promise<unknown> {
    const method = init?.method ?? 'GET';
    const includeBody = method !== 'GET' && method !== 'HEAD';
    const includeJsonBody = includeBody && !(init?.body instanceof FormData);
    const headers = {
      ...this.buildHeaders(includeJsonBody, requireAuth),
      ...(init?.headers ?? {}),
    };

    const response = await fetch(this.toAbsoluteUrl(path), {
      ...init,
      headers,
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(this.toErrorMessage(payload, `Request failed with status ${response.status}`));
    }

    return payload;
  }

  private async postFormData(
    candidatePaths: string[],
    buildPayload: () => FormData,
    requireAuth = false,
  ): Promise<unknown> {
    let lastError: Error | null = null;

    for (const path of candidatePaths) {
      const response = await fetch(this.toAbsoluteUrl(path), {
        method: 'POST',
        headers: this.buildHeaders(false, requireAuth),
        body: buildPayload(),
      });

      let payload: unknown = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (response.ok) {
        return payload;
      }

      const message = this.toErrorMessage(payload, `Request failed with status ${response.status}`);
      if (response.status === 404 || response.status === 405) {
        lastError = new Error(message);
        continue;
      }

      throw new Error(message);
    }

    throw (lastError ?? new Error('Unable to upload media.'));
  }

  private updateCategoryCache(categories: Category[]): void {
    this.categoryCache = new Map(categories.map((category) => [category.id, category]));
  }

  async getCatalogSnapshot(): Promise<{ categories: Category[]; products: Product[] }> {
    const [categoriesResult, productsResult, audioResult, animationsResult] = await Promise.allSettled([
      this.request('/categories'),
      this.request('/books'),
      this.request('/audio'),
      this.request('/books/made-into-animation'),
    ]);

    const hasPrimaryProductSource =
      productsResult.status === 'fulfilled' ||
      audioResult.status === 'fulfilled' ||
      animationsResult.status === 'fulfilled';
    if (!hasPrimaryProductSource) {
      throw new Error('Failed to load products.');
    }

    const categoriesPayload = categoriesResult.status === 'fulfilled' ? categoriesResult.value : [];
    const categories = this.normalizeCategoryList(categoriesPayload);
    this.updateCategoryCache(categories);
    const mergedProducts = new Map<string, Product>();
    const upsertProducts = (incomingProducts: Product[]): void => {
      incomingProducts.forEach((incomingProduct) => {
        const existingProduct = mergedProducts.get(incomingProduct.id);
        if (!existingProduct) {
          mergedProducts.set(incomingProduct.id, incomingProduct);
          return;
        }

        mergedProducts.set(incomingProduct.id, {
          ...existingProduct,
          ...incomingProduct,
          mediaType: incomingProduct.mediaType ?? existingProduct.mediaType,
        });
      });
    };

    if (productsResult.status === 'fulfilled') {
      upsertProducts(this.normalizeProductList(productsResult.value));
    }

    if (audioResult.status === 'fulfilled') {
      upsertProducts(
        this.normalizeProductList(audioResult.value).map((product) => ({
          ...product,
          mediaType: 'audio',
        })),
      );
    }

    if (animationsResult.status === 'fulfilled') {
      upsertProducts(
        this.normalizeProductList(animationsResult.value).map((product) => ({
          ...product,
          mediaType: 'animation',
        })),
      );
    }

    return { categories, products: [...mergedProducts.values()] };
  }

  async getCategories(): Promise<Category[]> {
    const payload = await this.request('/categories');
    const categories = this.normalizeCategoryList(payload);
    this.updateCategoryCache(categories);
    return categories;
  }

  async getCategoryById(id: string): Promise<Category | null> {
    const payload = await this.request(`/categories/${encodeURIComponent(id)}`);
    const category = this.normalizeSingleCategory(payload);
    if (!category) {
      return null;
    }

    this.categoryCache.set(category.id, category);
    return category;
  }

  async createCategory(input: CategoryMutationPayload): Promise<Category | null> {
    const payload = await this.request(
      '/categories',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true,
    );
    const category = this.normalizeSingleCategory(payload);
    if (!category) {
      return null;
    }

    this.categoryCache.set(category.id, category);
    return category;
  }

  async updateCategory(id: string, input: Partial<CategoryMutationPayload>): Promise<Category | null> {
    const payload = await this.request(
      `/categories/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
      true,
    );
    const category = this.normalizeSingleCategory(payload);
    if (!category) {
      return null;
    }

    this.categoryCache.set(category.id, category);
    return category;
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request(
      `/categories/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
      true,
    );
    this.categoryCache.delete(id);
  }

  async getProducts(query?: CatalogQuery): Promise<Product[]> {
    const payload = await this.request(`/books${this.buildQueryString(query)}`);
    return this.normalizeProductList(payload);
  }

  async getAnimations(query?: CatalogQuery): Promise<Product[]> {
    const payload = await this.request(`/books/made-into-animation${this.buildQueryString(query)}`);
    const animations = this.normalizeProductList(payload);
    return animations.map((animation) => ({
      ...animation,
      mediaType: 'animation',
    }));
  }

  async getFeaturedProducts(): Promise<Product[]> {
    const payload = await this.request('/books/approved');
    return this.normalizeProductList(payload);
  }

  async getProductById(id: string, requireAuth = false): Promise<Product | null> {
    const payload = await this.request(`/books/${encodeURIComponent(id)}`, undefined, requireAuth);
    return this.normalizeSingleProduct(payload);
  }

  async createProduct(input: ProductMutationPayload): Promise<Product | null> {
    const payload = await this.request(
      '/books',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true,
    );
    return this.normalizeSingleProduct(payload);
  }

  async createCatalogItem(
    input: CatalogItemCreateInput,
    onUploadProgress?: (progress: UploadProgress) => void,
  ): Promise<CatalogItemCreateResult> {
    const categoryKey = this.asString(input.categoryKey);
    if (!categoryKey) {
      throw new Error('categoryKey is required.');
    }

    const formData = new FormData();
    formData.append('kind', input.kind);
    formData.append('categoryKey', categoryKey);
    formData.append('titleKey', input.titleKey);
    formData.append('descriptionKey', input.descriptionKey);
    // Backend accepts aliases; send both for safety.
    formData.append('name', input.titleKey);
    formData.append('overview', input.descriptionKey);

    if (input.audience) {
      formData.append('audience', input.audience);
    }
    if (typeof input.price === 'number' && Number.isFinite(input.price)) {
      const normalizedPrice = Number(input.price.toFixed(2));
      formData.append('price', String(normalizedPrice));
      formData.append('cost', String(normalizedPrice)); // alias
    }
    if (typeof input.printPrice === 'number' && Number.isFinite(input.printPrice)) {
      formData.append('printPrice', String(Number(input.printPrice.toFixed(2))));
    }
    if (input.currency) {
      formData.append('currency', input.currency);
    }
    if (input.language) {
      formData.append('language', input.language);
    }
    if (input.isPODAvailable !== undefined) {
      formData.append('isPODAvailable', input.isPODAvailable ? 'true' : 'false');
    }
    if (input.podProjectId) {
      formData.append('podProjectId', input.podProjectId);
    }
    if (input.slug) {
      formData.append('slug', input.slug);
    }
    if (input.imageUrl) {
      formData.append('imageUrl', input.imageUrl);
      formData.append('coverUrl', input.imageUrl);
    }
    if (input.contentUrl) {
      formData.append('contentUrl', input.contentUrl);
    }
    if (input.status) {
      formData.append('status', input.status);
    }

    // Kind-specific payload
    if (input.kind === 'book') {
      if (!input.bookFile) {
        throw new Error('bookFile is required for kind=book.');
      }
      formData.append('bookFile', input.bookFile);
      formData.append('bookFormat', input.bookFormat ?? 'digital');
      if (input.coverFile) {
        formData.append('coverFile', input.coverFile);
      }
    }

    if (input.kind === 'audio') {
      if (!input.audioFile) {
        throw new Error('audioFile is required for kind=audio.');
      }
      formData.append('audioFile', input.audioFile);
      if (input.coverFile) {
        formData.append('coverFile', input.coverFile);
      }
    }

    if (input.kind === 'animation') {
      const trailer = input.trailerFile ?? input.thumbnailFile;
      const fullVideo = input.fullVideoFile ?? input.videoFile;
      const picture = input.pictureFile ?? input.coverFile;
      if (!trailer) {
        throw new Error('trailerFile is required for kind=animation.');
      }
      if (!fullVideo) {
        throw new Error('fullVideoFile is required for kind=animation.');
      }
      if (!picture) {
        throw new Error('coverFile is required for kind=animation.');
      }
      formData.append('trailerFile', trailer);
      formData.append('fullVideoFile', fullVideo);
      // Send as both picture and cover so backend can map to imageUrl.
      formData.append('pictureFile', picture);
      formData.append('coverFile', picture);
    }

    const payload = onUploadProgress
      ? await this.postFormDataWithProgress('/products', formData, onUploadProgress, true)
      : await this.request(
          '/products',
          {
            method: 'POST',
            body: formData,
          },
          true,
        );

    return {
      kind: input.kind,
      product: this.normalizeSingleProduct(payload),
    };
  }

  async uploadAnimation(input: AnimationUploadPayload): Promise<void> {
    const titleKey = this.asString(input.titleKey);
    const descriptionKey = this.asString(input.descriptionKey);
    if (!titleKey || !descriptionKey || !(input.videoFile instanceof File)) {
      throw new Error('Animation titleKey, descriptionKey, and video file are required.');
    }

    await this.postFormData(
      ['/animations', '/animations/upload', '/animation'],
      () => {
        const formData = new FormData();
        formData.append('titleKey', titleKey);
        formData.append('descriptionKey', descriptionKey);
        formData.append('videoFile', input.videoFile);

        if (input.thumbnailFile instanceof File) {
          formData.append('thumbnailFile', input.thumbnailFile);
        }

        const categoryKey = this.asString(input.categoryKey);
        if (categoryKey) {
          formData.append('categoryKey', categoryKey);
        }

        const language = this.asString(input.language);
        if (language) {
          formData.append('language', language);
        }

        const audience = this.asString(input.audience);
        if (audience) {
          formData.append('audience', audience);
        }

        return formData;
      },
      true,
    );
  }

  async uploadAudio(input: AudioUploadPayload): Promise<void> {
    const titleKey = this.asString(input.titleKey);
    const descriptionKey = this.asString(input.descriptionKey);
    if (!titleKey || !descriptionKey || !(input.audioFile instanceof File)) {
      throw new Error('Audio titleKey, descriptionKey, and audio file are required.');
    }

    await this.postFormData(
      ['/audio', '/audios', '/audio/upload'],
      () => {
        const formData = new FormData();
        formData.append('titleKey', titleKey);
        formData.append('descriptionKey', descriptionKey);
        formData.append('audioFile', input.audioFile);

        if (input.thumbnailFile instanceof File) {
          formData.append('thumbnailFile', input.thumbnailFile);
        }

        const categoryKey = this.asString(input.categoryKey);
        if (categoryKey) {
          formData.append('categoryKey', categoryKey);
        }

        const language = this.asString(input.language);
        if (language) {
          formData.append('language', language);
        }

        const audience = this.asString(input.audience);
        if (audience) {
          formData.append('audience', audience);
        }

        return formData;
      },
      true,
    );
  }

  async updateProduct(id: string, input: Partial<ProductMutationPayload>): Promise<Product | null> {
    const payload = await this.request(
      `/products/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
      true,
    );
    return this.normalizeSingleProduct(payload);
  }

  async deleteProduct(id: string): Promise<void> {
    await this.request(
      `/products/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
      true,
    );
  }
}

export const catalogService = new CatalogService();
export { CatalogService };
